import React, { useEffect, useState } from "react";
import {
  getCustomVoucher,
  getTrackingEvent,
  postTrackingBundle,
} from "../../../../services/offers/offers.service";
import {
  CampaignType,
  MediaType,
  Offer,
  OfferResolutionMedia,
  OffersIdSlots,
  OtherSettingsInterface,
  PlacementStage,
  QueryBundle,
  RedeemBy,
  Template,
  TemplateIcons,
  TemplateOptionSettings,
  LayoutOptions,
  VoucherType,
  ICONS_DEFAULT_VALUES,
} from "../../../../types/global.types";
import offerImageSrc from "./../../../../assets/images/offer-listening.png";
import TemplateIcon from "../../components/Icons/info.component";
import BlockedTag from "../../components/BlockedTag/blockedTag.component";
import PreSelect from "../../components/PreSelect/PreSelect";
import ImageCarousel from "../../../ImageCarousel/ImageCarousel.component";

interface Props {
  offers: Offer[];
  eventOfferIds: OffersIdSlots[];
  settings: TemplateOptionSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  sessionId: string;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  otherSettings: OtherSettingsInterface | undefined;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => Promise<void>;
  autoSendingOfferId?: string;
}

const OfferItem = ({
  offers,
  eventOfferIds,
  settings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  openSingle,
  onAdd,
  otherSettings,
  sessionId,
  layoutId,
  layoutOptions,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
}: Props) => {
  const [platform, setPlatform] = useState("");
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);

  const getOfferSlotById = (
    offerId: string,
    offerIdAndSlots: OffersIdSlots[]
  ): number => offerIdAndSlots.find((f) => f.id === offerId)?.slot || 0;

  const handleAdd = (
    e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLElement>,
    offer: Offer,
    value: boolean
  ) => {
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput(offer);
    }
  };

  const handleOpenEmailInput = (offer: Offer) => {
    if (
      campaign.campaignGeneralOptions?.showAddToOrder &&
      campaign.campaignGeneralOptions?.emailCaptureOnOffers &&
      campaign.campaignGeneralOptions?.emailCaptureOnAddToOrder
    ) {
      const slot = getOfferSlotById(offer.id, eventOfferIds) || 0;
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string,
    slot: number,
    offer: Offer
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("embedded-compact-info") &&
      !event.target.classList.contains("embedded-compact-share") &&
      !event.target.classList.contains("embedded-compact-email") &&
      !event.target.classList.contains("copy-box") &&
      !displayOnly &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      if (offer.offerSettings.bundleOptions.isBundled) {
        const data: QueryBundle = {
          offerId: offer.id,
          campaignId: campaign.id,
          placementId: placementId,
          orderId: offer.orderId,
          userSessionId: sessionId,
          imageUrl: getImageUrl(offer),
          pageUrl: window.location.href,
          templateId: settingsTemplate.identifier,
          placementName: placementName,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          slot: slot,
          layoutId: layoutId,
        };

        const response = await postTrackingBundle(data);

        if (response?.data?.status) {
          window.open(`/bundle-offer/${response?.data?.result}`, "_blank");
        }
      } else {
        window.open(
          url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
          "_blank"
        );
      }
    }
  };

  const handleClickInfo = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    const slot = getOfferSlotById(offer.id, eventOfferIds);
    openSingle(offer, false, false, slot, true);
  };

  const handleSaveEmail = async (
    event: React.MouseEvent | React.KeyboardEvent,
    offer: Offer
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const offerSlot = getOfferSlotById(offer.id, eventOfferIds);
    if (
      canAutoSendEmailFromLabel &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled
    ) {
      await onAutoSendEmailFromLabel(offer, offerSlot);
      return;
    }
    openSingle(offer, true, false, offerSlot);
  };

  const handleMainCtaClick = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    offer: Offer,
    offerSlot: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (
      canAutoSendEmailFromMainCTA &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      autoSendingOfferId !== offer.id
    ) {
      void onAutoSendEmailFromLabel(offer, offerSlot);
    }
    void handleGetGift(event, offer.trackingUrl, offerSlot, offer);
  };

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    // Check if the platform is iOS
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform("ios");
    }
    // Check if the platform is macOS
    else if (/macintosh|macintel|macppc|mac68k|macos/.test(userAgent)) {
      setPlatform("mac");
    } else {
      setPlatform("other");
    }
  }, []);

  const getMediaResolutionByType = (
    type: MediaType,
    offer: Offer
  ): OfferResolutionMedia | undefined => {
    let media: OfferResolutionMedia | undefined = offer.media.resolution.find(
      (r) => r.type === type
    );

    return media;
  };

  const getImageUrl = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media && media.images.length
      ? media.images[0].filepath
      : offer.imageUrl || offerImageSrc;
  };

  const getImages = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media && media.images.length
      ? media.images.map((image) => image.filepath)
      : offer.media.mainImage.length > 0
        ? offer.media.mainImage.map((image) => image.filepath)
        : [offer.imageUrl || offerImageSrc];
  };

  const getTitle = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);
    return media?.title || offer.media.featured.title || offer.title;
  };

  const getDescription = (offer: Offer) => {
    const media = getMediaResolutionByType(MediaType.main, offer);

    return (
      media?.description ||
      offer.media.featured.description ||
      offer.description
    );
  };

  const handleCopyHtml = (offer: Offer) => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async (offer: Offer) => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const getVoucherText = (offer: Offer) => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  return (
    <>
      <div
        className={`swiper-item ${displayOnly && campaign.campaignGeneralOptions?.showAddToOrder
            ? "display"
            : "getbutton"
          }`}
      >
        {offers.map((offer: Offer) => {
          return (
            <div
              key={`embedded-compact-${offer.id}`}
              onClick={(e) =>
                handleGetGift(
                  e,
                  offer.trackingUrl,
                  getOfferSlotById(offer.id, eventOfferIds),
                  offer
                )
              }
              className={`swiper-embedded-compact-item ${displayOnly && campaign.campaignGeneralOptions?.showAddToOrder
                  ? "display"
                  : "getbutton"
                }`}
            >
              <input
                className="input"
                id={offer.id}
                type="checkbox"
                disabled={
                  !displayOnly ||
                  !campaign.campaignGeneralOptions?.showAddToOrder
                }
                checked={!displayOnly ? false : offer.isAdded || false}
                onChange={(e) => handleAdd(e, offer, !offer.isAdded || false)}
              />
              {(layoutOptions?.showBadge ?? true) && (
                <span
                  style={{
                    position: "absolute",
                    top: "-8.5px",
                    left: "10px",
                    fontSize: "10px",
                    textTransform: "uppercase",
                    color: offer.badge?.fontColor,
                    backgroundColor: offer.badge?.backgroundColor,
                    padding: "0 6px",
                    borderRadius: "3px",
                    fontWeight: "600",
                    zIndex: 1,
                  }}
                  className="badge-settings"
                >
                  {offer.badge?.name}
                </span>
              )}
              <label
                htmlFor={offer.id}
                className="embedded-compact-item-layout"
                tabIndex={0}
                role="presentation"
                aria-label="Offer item"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    displayOnly
                      ? handleAdd(e, offer, !offer.isAdded || false)
                      : handleGetGift(
                        e,
                        offer.trackingUrl,
                        getOfferSlotById(offer.id, eventOfferIds),
                        offer
                      );
                  }
                }}
                style={{
                  backgroundColor: settings?.dealLayout?.backgroundColor,
                  fontSize: settings?.dealLayout?.fontSize,
                  color: settings?.dealLayout?.textColor,
                  border: offer.isAdded
                    ? `1px solid ${settings.mainLayout?.highlightColor}`
                    : offer.badge?.useBorder &&
                      (layoutOptions?.showBadge ?? true)
                      ? `1px solid ${offer.badge?.backgroundColor}`
                      : settings.dealLayout?.borderColor
                        ? `1px solid ${settings.dealLayout?.borderColor}`
                        : "#ddd",
                  borderRadius: `${settings.mainLayout?.borderRadius}px`,
                  position: "relative",
                }}
              >
                {offer.tagBlocked.isBlocked && (
                  <BlockedTag tagBlocked={offer.tagBlocked} />
                )}

                <div
                  className="embedded-compact-border"
                  style={{ borderColor: settings?.mainLayout?.highlightColor }}
                ></div>

                <div
                  className="embedded-compact-item-image"
                  style={{
                    borderRadius: `${settings?.mainLayout?.borderRadius}px`,
                  }}
                >
                  <ImageCarousel
                    images={getImages(offer)}
                    showPagination={true}
                    borderRadius={settings?.mainLayout?.borderRadius}
                    altPrefix="offer"
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>

                <div
                  className={`embedded-compact-item-content`}
                  style={{
                    marginBottom: offer.voucher?.voucherType ? "18px" : "",
                  }}
                >
                  <div className="embedded-compact-item-content-inner">
                    <div className="embedded-compact-item-title">
                      <span
                        className={`embedded-compact-item-title-text max-2lines `}
                        style={{
                          color: settings?.dealLayout?.textColor,
                          fontSize: `${settings?.dealLayout?.fontSize}px`,
                        }}
                        tabIndex={0}
                        role="contentinfo"
                        aria-label={getTitle(offer)}
                      >
                        {getTitle(offer)}
                      </span>
                    </div>

                    {!settings?.dealLayout.showHeader && (
                      <div
                        className="embedded-compact-item-description"
                        style={{
                          color: settings?.dealLayout.textColor,
                          fontSize: `${Number(settings?.dealLayout.fontSize) - 2
                            }px`,
                        }}
                      >
                        <span
                          className="max-2lines"
                          tabIndex={0}
                          role="contentinfo"
                          aria-label={getDescription(offer)}
                        >
                          {getDescription(offer)}
                        </span>
                      </div>
                    )}

                    <div
                      className="embedded-compact-item-get"
                      style={{
                        justifyContent:
                          settings?.dealLayout.buttonAlign === "left"
                            ? "flex-start"
                            : settings?.dealLayout.buttonAlign === "right"
                              ? "flex-end"
                              : "center",
                      }}
                    >
                      {!displayOnly && (
                        <>
                          {offer.offerSettings.redeemBy === RedeemBy.email ? (
                            offer?.voucher?.voucherType ===
                              VoucherType.CUSTOM && !getCode ? (
                              <div
                                className="dashed-button-embedded-compact copy-box"
                                onClick={() => handleGetCode(offer)}
                                tabIndex={0}
                                role="button"
                                aria-label="Get code"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                    handleGetCode(offer);
                                  }
                                }}
                                style={{
                                  fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                                  width: `${settings?.dealLayout.buttonWidthValue ||
                                    "100"
                                    }${settings?.dealLayout.buttonWidthType || "%"
                                    }`,
                                }}
                              >
                                {voucherLoading ? (
                                  <svg
                                    className="spin"
                                    viewBox="0 0 1024 1024"
                                    focusable="false"
                                    data-icon="loading"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    aria-hidden="true"
                                  >
                                    <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                                  </svg>
                                ) : (
                                  "Get code"
                                )}
                              </div>
                            ) : (
                              <div
                                className="compact-item-action-get"
                                onClick={(e) => handleSaveEmail(e, offer)}
                                tabIndex={0}
                                role="button"
                                aria-label="Save offer to email"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.stopPropagation();
                                    handleSaveEmail(e, offer);
                                  }
                                }}
                                style={{
                                  backgroundColor:
                                    settings?.dealLayout
                                      ?.emailButtonBackgroundColor,
                                  color:
                                    settings?.dealLayout?.emailButtonTextColor,
                                  marginRight: "0",
                                  fontSize: "14px",
                                  border: `1px solid transparent`,
                                  borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                                }}
                              >
                                <TemplateIcon
                                  icon={
                                    settings?.dealLayout?.iconEmail?.value ||
                                    ICONS_DEFAULT_VALUES.iconEmail
                                  }
                                  color={
                                    settings?.dealLayout
                                      ?.emailButtonTextColor || "#888888"
                                  }
                                />
                                {offer.offerCTAText}
                              </div>
                            )
                          ) : offer?.voucher?.voucherType ===
                            VoucherType.CUSTOM && !getCode ? (
                            <div
                              className="dashed-button-embedded-compact copy-box"
                              onClick={() => handleGetCode(offer)}
                              tabIndex={0}
                              role="button"
                              aria-label="Get code"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.stopPropagation();
                                  handleGetCode(offer);
                                }
                              }}
                              style={{
                                fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                                width: `${settings?.dealLayout.buttonWidthValue || "100"
                                  }${settings?.dealLayout.buttonWidthType || "%"
                                  }`,
                              }}
                            >
                              {voucherLoading ? (
                                <svg
                                  className="spin"
                                  viewBox="0 0 1024 1024"
                                  focusable="false"
                                  data-icon="loading"
                                  width="1em"
                                  height="1em"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                                </svg>
                              ) : (
                                "Get code"
                              )}
                            </div>
                          ) : (
                            <div
                              className="embedded-compact-item-action-get"
                              tabIndex={0}
                              role="button"
                              aria-label="Get offer now"
                              onClick={(e) =>
                                handleMainCtaClick(
                                  e,
                                  offer,
                                  getOfferSlotById(offer.id, eventOfferIds)
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleMainCtaClick(
                                    e,
                                    offer,
                                    getOfferSlotById(offer.id, eventOfferIds)
                                  );
                                }
                              }}
                              style={{
                                color: settings?.dealLayout?.buttonTextColor,
                                fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                                fontWeight:
                                  settings?.dealLayout?.buttonFontWeight,
                                backgroundColor:
                                  settings?.dealLayout?.buttonBackgroundColor,
                                borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                                border: `1px solid ${settings?.dealLayout?.buttonBorderColor ||
                                  settings?.dealLayout?.buttonBackgroundColor
                                  }`,
                                textTransform:
                                  settings?.dealLayout.buttonTextTransform,
                                width: `${settings?.dealLayout.buttonWidthValue || "100"
                                  }${settings?.dealLayout.buttonWidthType || "%"
                                  }`,
                              }}
                            >
                              {offer.offerCTAText || "Get offer now"}
                            </div>
                          )}
                        </>
                      )}

                      {displayOnly &&
                        campaign.campaignGeneralOptions.showAddToOrder && (
                          <PreSelect
                            offerId={offer.id}
                            settings={otherSettings}
                            buttonFontWeight={
                              settings?.dealLayout.buttonFontWeight
                            }
                            buttonFontSize={settings?.dealLayout.buttonFontSize}
                            showCheckboxSelect={
                              otherSettings?.showCheckboxSelect
                            }
                            checked={offer.isAdded || false}
                            highlightColor={settings.mainLayout?.highlightColor}
                            selectedButtonText={
                              otherSettings?.selectedButtonText
                            }
                            notSelectedButtonText={
                              otherSettings?.notSelectedButtonText
                            }
                            textColor={otherSettings?.textColor}
                          />
                        )}
                    </div>

                    {/* {displayOnly ? (
                        <>
                          <div className="switch-wrapper">
                            {campaign.campaignGeneralOptions
                              ?.showAddToOrder && (
                                <label htmlFor={offer.id}>
                                  <span
                                    className="switch-toggle"
                                    style={{
                                      backgroundColor: offer.isAdded
                                        ? settings?.mainLayout?.highlightColor
                                        : "#f1f2f3",
                                    }}
                                  >
                                    <span className="switch-toggle-before"></span>
                                  </span>

                                  <span
                                    className="switch-text"
                                    style={{
                                      fontWeight:
                                        settings?.dealLayout?.buttonFontWeight,
                                      fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                                    }}
                                  >
                                    <span className="checked-text">Selected</span>
                                    <span className="unchecked-text">Select</span>
                                  </span>
                                </label>
                              )}
                          </div>
                        </>
                      ) : (
                        <>
                          {offer.offerSettings.redeemBy === RedeemBy.email ? (
                            <div
                              className="embedded-compact-item-action-get"
                              onClick={(e) => handleSaveEmail(e, offer)}
                              style={{
                                backgroundColor:
                                  settings?.dealLayout
                                    ?.emailButtonBackgroundColor,
                                color:
                                  settings?.dealLayout?.emailButtonTextColor,
                                marginRight: "0",
                                fontSize: "14px",
                                border: `1px solid transparent`,
                                borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                              }}
                            >
                              <svg
                                style={{
                                  marginRight: "5px",
                                  minWidth: "14px",
                                  verticalAlign: "middle",
                                }}
                                width="14"
                                height="11"
                                viewBox="0 0 14 11"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13 2.125C13 1.50625 12.46 1 11.8 1H2.2C1.54 1 1 1.50625 1 2.125M13 2.125V8.875C13 9.49375 12.46 10 11.8 10H2.2C1.54 10 1 9.49375 1 8.875V2.125M13 2.125L7 6.0625L1 2.125"
                                  stroke={
                                    settings?.dealLayout?.emailButtonTextColor
                                  }
                                  strokeWidth="1"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {offer.offerCTAText}
                            </div>
                          ) : (
                            <div
                              className="embedded-compact-item-action-get button max-2lines"
                              style={{
                                color: settings?.dealLayout?.buttonTextColor,
                                fontSize: `${settings?.dealLayout?.buttonFontSize}px`,
                                fontWeight:
                                  settings?.dealLayout?.buttonFontWeight,
                                backgroundColor:
                                  settings?.dealLayout?.buttonBackgroundColor,
                                borderRadius: `${settings?.dealLayout?.buttonRounding}px`,
                                border: `1px solid ${settings?.dealLayout?.buttonBorderColor ||
                                  settings?.dealLayout?.buttonBackgroundColor
                                  }`,
                              }}
                            >
                              {offer.offerCTAText || "Get offer now"}
                            </div>
                          )}
                        </>
                      )} */}
                  </div>
                  <div className="embedded-compact-item-icons">
                    <div
                      className="embedded-compact-info"
                      onClick={(e) => handleClickInfo(e, offer)}
                      tabIndex={0}
                      role="button"
                      aria-label="More information about offer"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          handleClickInfo(e, offer);
                        }
                      }}
                    >
                      {!settings?.dealLayout?.icons ||
                        settings?.dealLayout?.icons === TemplateIcons.default ? (
                        <TemplateIcon
                          icon={
                            settings?.dealLayout?.iconInfo?.value ||
                            ICONS_DEFAULT_VALUES.iconInfo
                          }
                          color={settings?.dealLayout?.textColor || "#888888"}
                        />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                            fill={settings?.dealLayout.textColor}
                          />
                        </svg>
                      )}
                    </div>

                    {!displayOnly &&
                      campaign.sharingOptions?.allowSharing &&
                      campaign.sharingOptions.sharingPlatforms?.platforms
                        ?.length > 0 &&
                      offer?.offerSettings?.allowShare &&
                      !offer.offerSettings?.bundleOptions?.isBundled &&
                      (layoutOptions?.showShare ?? true) && (
                        <div
                          className="embedded-compact-share"
                          onClick={(e) => handleClickShare(e, offer)}
                          tabIndex={0}
                          role="button"
                          aria-label="Share offer"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleClickShare(e, offer);
                            }
                          }}
                        >
                          {platform === "ios" || platform === "mac" ? (
                            <svg
                              width="20"
                              height="28"
                              viewBox="0 0 20 28"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15.6638 6.41895L14.6218 7.49902L10.7497 3.76465V18.7139H9.24973V3.76465L5.37766 7.49902L4.33664 6.41895L9.99973 0.958008L15.6638 6.41895Z"
                                fill={settings?.dealLayout.textColor}
                                stroke={settings?.dealLayout.textColor}
                                strokeWidth="0.5"
                              />
                              <path
                                d="M0.25 22.5713V14.8574C0.25 12.3129 2.31298 10.25 4.85742 10.25H6.78613V11.75H4.85742C3.14139 11.75 1.75 13.1413 1.75 14.8574V22.5713C1.75 24.2874 3.14139 25.6787 4.85742 25.6787H15.1426C16.8587 25.6787 18.25 24.2874 18.25 22.5713V14.8574C18.25 13.1413 16.8587 11.75 15.1426 11.75H13.2139V10.25H15.1426C17.6871 10.25 19.75 12.3129 19.75 14.8574V22.5713C19.75 25.1158 17.6871 27.1787 15.1426 27.1787H4.85742C2.31298 27.1787 0.25 25.1158 0.25 22.5713Z"
                                fill={settings?.dealLayout.textColor}
                                stroke={settings?.dealLayout.textColor}
                                strokeWidth="0.5"
                              />
                            </svg>
                          ) : (
                            <TemplateIcon
                              icon={
                                settings?.dealLayout?.iconShare?.value ||
                                ICONS_DEFAULT_VALUES.iconShare
                              }
                              color={
                                settings?.dealLayout.textColor || "#888888"
                              }
                            />
                          )}
                        </div>
                      )}

                    {!displayOnly &&
                      campaign.campaignGeneralOptions.saveOfferOptions
                        .allowSavingOffers &&
                      campaign.campaignGeneralOptions.saveOfferOptions
                        .allowEmail &&
                      !offer.offerSettings?.bundleOptions?.isBundled &&
                      offer.offerSettings.redeemBy === RedeemBy.webemail &&
                      (layoutOptions?.showEmail ?? true) && (
                        <span
                          className="embedded-compact-email"
                          onClick={(e) => {
                            void handleSaveEmail(e, offer);
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={
                            autoSendingOfferId === offer.id
                              ? "Sending email"
                              : "Save offer to email"
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              void handleSaveEmail(e, offer);
                            }
                          }}
                          title={
                            autoSendingOfferId === offer.id
                              ? "Sending email..."
                              : undefined
                          }
                        >
                          {!settings?.dealLayout?.icons ||
                            settings?.dealLayout?.icons ===
                            TemplateIcons.default ? (
                            <TemplateIcon
                              icon={
                                settings?.dealLayout?.iconEmail?.value ||
                                ICONS_DEFAULT_VALUES.iconEmail
                              }
                              color={
                                settings?.dealLayout.textColor || "#888888"
                              }
                            />
                          ) : (
                            <svg
                              width="20"
                              height="16"
                              viewBox="0 0 20 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19 0H1C0.45 0 0 0.45 0 1V15C0 15.55 0.45 16 1 16H19C19.55 16 20 15.55 20 15V1C20 0.45 19.55 0 19 0ZM18.38 1L10 9.07996L1.69 1H18.38ZM1 15V1.72L10 10.47L19 1.8V15H1Z"
                                fill={
                                  settings?.dealLayout?.textColor || "#888888"
                                }
                              />
                            </svg>
                          )}
                        </span>
                      )}
                  </div>
                </div>
                {/* {displayOnly && (
                  <div className="embedded-compact-item-switch">
                    {campaign.campaignGeneralOptions?.showAddToOrder &&
                      (otherSettings?.showCheckboxSelect ? (
                        <div className="embedded-compact-item-switch">
                          <label
                            htmlFor={offer.id}
                            role="button"
                            aria-label={
                              offer.isAdded ? "Unselect offer" : "Select offer"
                            }
                          >
                            <div className="embedded-compact-select">
                              <div
                                className={`embedded-compact-checkbox ${
                                  offer.isAdded ? "checked" : ""
                                }`}
                                style={{
                                  backgroundColor: offer.isAdded
                                    ? settings?.mainLayout.highlightColor
                                    : "transparent",
                                  borderColor: offer.isAdded
                                    ? settings?.mainLayout?.highlightColor
                                    : settings.dealLayout?.borderColor ||
                                      "#ddd",
                                }}
                              >
                                {offer.isAdded && (
                                  <svg
                                    width="12"
                                    height="9"
                                    viewBox="0 0 12 9"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M11 1L4 8L1 5"
                                      stroke="white"
                                      strokeWidth="1.2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ) : (
                        <div className="embedded-compact-switch-wrapper">
                          <label
                            htmlFor={offer.id}
                            tabIndex={0}
                            role="button"
                            aria-label={
                              offer.isAdded ? "Unselect offer" : "Select offer"
                            }
                          >
                            <span
                              className={
                                offer.isAdded
                                  ? "embedded-compact-switch-toggle embedded-compact-switch-toggle--selected"
                                  : "embedded-compact-switch-toggle"
                              }
                              style={{
                                backgroundColor: offer.isAdded
                                  ? settings?.mainLayout?.highlightColor
                                  : "#f1f2f3",
                              }}
                            >
                              <span
                                className={
                                  offer.isAdded
                                    ? "embedded-compact-switch-toggle-before embedded-compact-switch-toggle-before--selected"
                                    : "embedded-compact-switch-toggle-before"
                                }
                              ></span>
                            </span>
                          </label>
                        </div>
                      ))}
                  </div>
                )} */}

                {offer.voucher?.voucherType && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "0",
                      fontSize: "11px",
                      color: settings?.dealLayout?.buttonTextColor,
                      backgroundColor:
                        settings?.dealLayout?.buttonBackgroundColor,
                      borderRadius: "0 0 5px 5px",
                      fontWeight: "600",
                      textAlign: "center",
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onMouseEnter={() => setCopyCode(true)}
                    onMouseLeave={() => {
                      setCopyCode(false);
                    }}
                    onClick={() => handleCopyHtml(offer)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCopyHtml(offer);
                      }
                    }}
                  >
                    {offer.voucher?.voucherType === VoucherType.CUSTOM &&
                      !getCode ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                        }}
                        className="copy-box"
                      >
                        Code:
                        <svg
                          style={{
                            marginLeft: "5px",
                          }}
                          width="70"
                          height="16"
                          viewBox="0 0 70 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="70" height="16" fill="" />
                          <circle
                            cx="4"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="10"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="52"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="58"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="16"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="22"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="28"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="34"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="40"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                          <circle
                            cx="46"
                            cy="8"
                            r="2"
                            fill={
                              settings?.dealLayout?.buttonTextColor || "white"
                            }
                          />
                        </svg>
                      </div>
                    ) : (
                      <>
                        {copyCode ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "100%",
                              fontWeight: "400",
                            }}
                            onClick={() => handleCopyHtml(offer)}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleCopyHtml(offer);
                              }
                            }}
                            className="copy-box"
                          >
                            {copied ? (
                              <>
                                <svg
                                  width="8"
                                  height="6"
                                  viewBox="0 0 8 6"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginRight: "5px",
                                  }}
                                >
                                  <path
                                    d="M1 3L3 5L7 1"
                                    stroke={
                                      settings?.dealLayout?.buttonTextColor ||
                                      "white"
                                    }
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Copied successfully
                              </>
                            ) : (
                              <>
                                <svg
                                  width="11"
                                  height="10"
                                  viewBox="0 0 11 10"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{
                                    marginRight: "5px",
                                  }}
                                >
                                  <path
                                    d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                                    stroke={
                                      settings?.dealLayout?.buttonTextColor ||
                                      "white"
                                    }
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                Copy Code
                              </>
                            )}
                          </div>
                        ) : copied ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              fontWeight: "400",
                            }}
                            className="copy-box"
                          >
                            <svg
                              width="8"
                              height="6"
                              viewBox="0 0 8 6"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{
                                marginRight: "5px",
                              }}
                            >
                              <path
                                d="M1 3L3 5L7 1"
                                stroke={
                                  settings?.dealLayout?.buttonTextColor ||
                                  "white"
                                }
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Copied successfully
                          </div>
                        ) : (
                          getVoucherText(offer)
                        )}
                      </>
                    )}
                  </span>
                )}
              </label>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default OfferItem;
